import { useRef, useState } from "react";
import type {
  PublicMapArea,
  PublicPlaceCandidate,
  PublicPlaceSearchResponse,
  PublicWaterbodyGeometryCandidate,
  PublicWaterbodyPreviewResponse,
} from "@natural/public-contract";
import { accountApi } from "../../account";

export function useOrganizationAreaClaims(areas: PublicMapArea[]) {
  const [claimCount, setClaimCount] = useState(1);
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([""]);
  const [areaSearch, setAreaSearch] = useState("");
  const [areaMatchesOpen, setAreaMatchesOpen] = useState(true);
  const [focusAreaId, setFocusAreaId] = useState<string | null>(null);
  const [areaDetailsOpen, setAreaDetailsOpen] = useState(false);
  const [selectedPlaces, setSelectedPlaces] = useState<
    Array<PublicPlaceCandidate | null>
  >([null]);
  const [waterbodyPreviews, setWaterbodyPreviews] = useState<
    Array<PublicWaterbodyGeometryCandidate | null>
  >([null]);
  const [waterbodyPreviewStates, setWaterbodyPreviewStates] = useState<string[]>([""]);
  const waterbodyPreviewRequests = useRef<string[]>([]);
  const [areaKinds, setAreaKinds] = useState(["area"]);
  const [parentAreaIds, setParentAreaIds] = useState([""]);
  const [placeCandidates, setPlaceCandidates] = useState<PublicPlaceCandidate[]>([]);
  const [placeSearchState, setPlaceSearchState] = useState<
    "" | "loading" | "empty" | "error"
  >();
  const [activeClaimIndex, setActiveClaimIndex] = useState(0);

  const matchingAreas =
    areaSearch.trim().length < 2
      ? []
      : areas
          .filter((area) =>
            area.name
              .toLocaleLowerCase("fi")
              .includes(areaSearch.trim().toLocaleLowerCase("fi")),
          )
          .slice(0, 8);

  const selectArea = (area: PublicMapArea) => {
    setFocusAreaId(area.id);
    setAreaDetailsOpen(true);
    setSelectedAreaIds((current) =>
      current.map((value, index) => (index === activeClaimIndex ? area.id : value)),
    );
    setSelectedPlaces((current) =>
      current.map((value, index) => (index === activeClaimIndex ? null : value)),
    );
    setAreaKinds((current) =>
      current.map((value, index) =>
        index === activeClaimIndex
          ? area.kind === "river"
            ? "river"
            : area.kind === "waterbody"
              ? "waterbody"
              : area.kind === "parent"
                ? "fishery_area"
                : "area"
          : value,
      ),
    );
  };

  const selectPlace = (place: PublicPlaceCandidate) => {
    setSelectedPlaces((current) =>
      current.map((value, index) => (index === activeClaimIndex ? place : value)),
    );
    setSelectedAreaIds((current) =>
      current.map((value, index) => (index === activeClaimIndex ? "" : value)),
    );
    setAreaKinds((current) =>
      current.map((value, index) =>
        index === activeClaimIndex
          ? place.placeTypeCode === "2020105"
            ? "river"
            : "waterbody"
          : value,
      ),
    );
    setAreaDetailsOpen(true);
    setPlaceCandidates([]);
    setAreaSearch("");
    setFocusAreaId(null);
    const claimIndex = activeClaimIndex;
    const requestId = `${place.provider}:${place.placeId}:${crypto.randomUUID()}`;
    waterbodyPreviewRequests.current[claimIndex] = requestId;
    setWaterbodyPreviews((current) =>
      current.map((value, index) => (index === claimIndex ? null : value)),
    );
    setWaterbodyPreviewStates((current) =>
      current.map((value, index) =>
        index === claimIndex ? "Muodostetaan vesistörajaa…" : value,
      ),
    );
    void accountApi<PublicWaterbodyPreviewResponse>("/api/public/waterbody-preview", {
      method: "POST",
      body: JSON.stringify({ place }),
    })
      .then((result) => {
        if (waterbodyPreviewRequests.current[claimIndex] !== requestId) return;
        setWaterbodyPreviews((current) =>
          current.map((value, index) =>
            index === claimIndex ? result.candidate : value,
          ),
        );
        setWaterbodyPreviewStates((current) =>
          current.map((value, index) =>
            index === claimIndex
              ? result.candidate
                ? "Vesistöraja löytyi"
                : "Vesistörajaa ei löytynyt; paikkapiste tallennetaan"
              : value,
          ),
        );
      })
      .catch(() => {
        if (waterbodyPreviewRequests.current[claimIndex] === requestId) {
          setWaterbodyPreviewStates((current) =>
            current.map((value, index) =>
              index === claimIndex
                ? "Vesistörajaa ei voitu näyttää; paikkapiste tallennetaan"
                : value,
            ),
          );
        }
      });
  };

  const searchPlaces = async () => {
    if (areaSearch.trim().length < 2) return;
    setAreaMatchesOpen(false);
    setPlaceSearchState("loading");
    try {
      const result = await accountApi<PublicPlaceSearchResponse>(
        `/api/public/places?name=${encodeURIComponent(areaSearch.trim())}`,
      );
      setPlaceCandidates(result.candidates ?? []);
      setPlaceSearchState(result.candidates?.length ? "" : "empty");
    } catch {
      setPlaceCandidates([]);
      setPlaceSearchState("error");
    }
  };

  const removeAreaClaim = (index: number) => {
    if (claimCount === 1) {
      setSelectedAreaIds([""]);
      setSelectedPlaces([null]);
      setWaterbodyPreviews([null]);
      setWaterbodyPreviewStates([""]);
      setAreaKinds(["area"]);
      setParentAreaIds([""]);
      return;
    }
    setClaimCount((value) => value - 1);
    setSelectedAreaIds((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setSelectedPlaces((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setWaterbodyPreviews((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setWaterbodyPreviewStates((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setAreaKinds((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setParentAreaIds((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setActiveClaimIndex(0);
  };

  const addAreaClaim = () => {
    if (claimCount >= 10) return;
    setClaimCount((value) => value + 1);
    setSelectedAreaIds((current) => [...current, ""]);
    setSelectedPlaces((current) => [...current, null]);
    setWaterbodyPreviews((current) => [...current, null]);
    setWaterbodyPreviewStates((current) => [...current, ""]);
    setAreaKinds((current) => [...current, "area"]);
    setParentAreaIds((current) => [...current, ""]);
    setActiveClaimIndex(claimCount);
  };

  return {
    activeClaimIndex,
    addAreaClaim,
    areaDetailsOpen,
    areaKinds,
    areaMatchesOpen,
    areaSearch,
    claimCount,
    focusAreaId,
    matchingAreas,
    parentAreaIds,
    placeCandidates,
    placeSearchState: placeSearchState ?? "",
    removeAreaClaim,
    searchPlaces,
    selectArea,
    selectedAreaIds,
    selectedPlaces,
    selectPlace,
    setActiveClaimIndex,
    setAreaDetailsOpen,
    setAreaKinds,
    setAreaMatchesOpen,
    setAreaSearch,
    setParentAreaIds,
    setPlaceCandidates,
    setPlaceSearchState,
    setSelectedAreaIds,
    waterbodyPreviews,
    waterbodyPreviewStates,
  };
}

export type OrganizationAreaClaimsController = ReturnType<
  typeof useOrganizationAreaClaims
>;
